import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Pulse } from "@phosphor-icons/react";

const STAGES = [
  { at: 0, label: "Generating 50 accounts", pct: 30 },
  { at: 1200, label: "Scoring recovery health", pct: 72 },
  { at: 2400, label: "Finding money stuck", pct: 100 },
];
const TOTAL = 3400;

// Moment 1 — cinematic full-screen branded loader. Deep-blue canvas, faint technical
// grid, staged status lines, sharp progress bar and a 0→50 monospace counter.
export function SampleLoader({ onDone }) {
  const [stage, setStage] = useState(0);
  const [pct, setPct] = useState(0);
  const [count, setCount] = useState(0);
  const raf = useRef(null);

  useEffect(() => {
    const t0 = performance.now();
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setStage(2); setPct(100); setCount(50); const d = setTimeout(onDone, 400); return () => clearTimeout(d); }

    const tick = (now) => {
      const e = now - t0;
      let s = 0;
      for (let i = 0; i < STAGES.length; i++) if (e >= STAGES[i].at) s = i;
      setStage(s);
      const progress = Math.min(1, e / TOTAL);
      // ease-out for a confident finish
      const eased = 1 - Math.pow(1 - progress, 2.2);
      setPct(Math.round(eased * 100));
      setCount(Math.min(50, Math.round(eased * 50)));
      if (e < TOTAL) { raf.current = requestAnimationFrame(tick); }
      else { setPct(100); setCount(50); setTimeout(onDone, 380); }
    };
    raf.current = requestAnimationFrame(tick);
    return () => raf.current && cancelAnimationFrame(raf.current);
  }, [onDone]);

  const done = stage >= 2;

  return (
    <motion.div
      data-testid="sample-loader"
      className="fixed inset-0 z-[130] flex flex-col items-center justify-center bg-[#0B3D91] overflow-hidden"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, scale: 1.05 }}
      transition={{ duration: 0.35 }}
      role="status" aria-live="polite"
    >
      {/* technical grid texture */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.10]"
        style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "42px 42px", maskImage: "radial-gradient(circle at center, #000 40%, transparent 78%)" }} />
      <div className="pointer-events-none absolute -top-32 -left-32 h-96 w-96 rounded-full bg-[#16A34A]/20 blur-[120px]" />

      <div className="relative z-10 w-full max-w-md px-8">
        <div className="flex items-center gap-2 text-white/60" style={{ fontFamily: "'Inter', sans-serif" }}>
          <Pulse size={16} weight="bold" className="text-[#7ef0a9]" />
          <span className="text-[11px] font-semibold uppercase tracking-[0.25em]">Building your sample portfolio</span>
        </div>

        <h2 key={stage} data-testid="sample-loader-stage"
          className="mt-5 text-3xl md:text-[2.6rem] leading-none font-black tracking-tight text-white"
          style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          {STAGES[stage].label}
          <motion.span initial={{ opacity: 0 }} animate={{ opacity: [0, 1, 0] }} transition={{ repeat: Infinity, duration: 1 }}>…</motion.span>
        </h2>

        <div className="mt-8 h-[3px] w-full bg-white/15 overflow-hidden rounded-none">
          <motion.div className="h-full bg-[#16A34A]" animate={{ width: `${pct}%` }} transition={{ ease: "linear", duration: 0.1 }} data-testid="sample-loader-bar" />
        </div>

        <div className="mt-4 flex items-center justify-between text-white/80" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          <span className="text-sm tabular-nums" style={{ fontVariantNumeric: "tabular-nums" }} data-testid="sample-loader-count">
            <span className={`font-black text-lg ${done ? "text-[#7ef0a9]" : "text-white"}`}>{count}</span>
            <span className="text-white/50"> / 50 accounts</span>
          </span>
          <span className="text-sm font-bold tabular-nums">{pct}%</span>
        </div>
      </div>
    </motion.div>
  );
}
